import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type {
  Adjustment,
  AppData,
  DocumentRecord,
  Expense,
  Lot,
  Product,
  Purchase,
  ReturnRecord,
  Sale,
  Settings,
  StockRow,
  Summary,
  TimelineEntry,
} from "@/lib/types";
import {
  clearStores,
  readAll,
  readSettings,
  STORE_NAMES,
  writeMany,
  writeSettings,
} from "@/lib/db";
import { buildExpiring, buildStock, buildSummary, buildTimeline, type ExpiringItem } from "@/lib/metrics";
import { buildSeedData, defaultSettings, emptyData } from "@/lib/seed";

interface DataContextValue {
  data: AppData;
  loading: boolean;
  error: string | null;
  summary: Summary;
  stock: StockRow[];
  expiring: ExpiringItem[];
  timeline: TimelineEntry[];
  reload: () => Promise<void>;
  addPurchase: (purchase: Purchase, lots: Lot[], newProducts?: Product[]) => Promise<void>;
  addSale: (sale: Sale) => Promise<void>;
  addExpense: (expense: Expense) => Promise<void>;
  addReturn: (record: ReturnRecord) => Promise<void>;
  addDocument: (doc: DocumentRecord) => Promise<void>;
  deleteDocument: (id: string) => Promise<void>;
  deletePurchase: (id: string) => Promise<void>;
  deleteSale: (id: string) => Promise<void>;
  deleteExpense: (id: string) => Promise<void>;
  deleteReturn: (id: string) => Promise<void>;
  deleteProduct: (productId: string) => Promise<void>;
  addAdjustment: (adj: Adjustment) => Promise<void>;
  upsertProduct: (product: Product) => Promise<void>;
  updateSettings: (patch: Partial<Settings>) => Promise<void>;
  resetAll: () => Promise<void>;
  loadDemoData: () => Promise<void>;
  importBackup: (incoming: AppData) => Promise<void>;
}

const DataContext = createContext<DataContextValue | null>(null);

async function persistAll(data: AppData): Promise<void> {
  const stores = STORE_NAMES.filter((s) => s !== "meta");
  await clearStores([...stores]);
  await Promise.all([
    writeMany("products", data.products),
    writeMany("lots", data.lots),
    writeMany("purchases", data.purchases),
    writeMany("sales", data.sales),
    writeMany("expenses", data.expenses),
    writeMany("returns", data.returns),
    writeMany("documents", data.documents),
    writeMany("adjustments", data.adjustments),
  ]);
}

function applyTheme(theme: Settings["theme"]): void {
  const root = document.documentElement;
  const prefersDark =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;
  const dark = theme === "dark" || (theme === "system" && prefersDark);
  root.classList.toggle("dark", dark);
}

export function DataProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<AppData>(() => emptyData());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setError(null);
      const [stored, settings] = await Promise.all([readAll(), readSettings()]);

      const next: AppData = {
        products: stored.products ?? [],
        lots: stored.lots ?? [],
        purchases: stored.purchases ?? [],
        sales: stored.sales ?? [],
        expenses: stored.expenses ?? [],
        returns: stored.returns ?? [],
        documents: stored.documents ?? [],
        adjustments: stored.adjustments ?? [],
        settings: settings ?? defaultSettings(),
      };

      setData(next);
    } catch (err) {
      setError("No se pudieron cargar los datos guardados en el dispositivo.");
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    applyTheme(data.settings.theme);
  }, [data.settings.theme]);

  const commit = useCallback(async (next: AppData) => {
    setData(next);
    await persistAll(next);
  }, []);

  const addPurchase = useCallback(
    async (purchase: Purchase, lots: Lot[], newProducts: Product[] = []) => {
      const next: AppData = {
        ...data,
        purchases: [...data.purchases, purchase],
        lots: [...data.lots, ...lots],
        products: [...data.products, ...newProducts.filter((p) => !data.products.some((x) => x.id === p.id))],
      };
      await commit(next);
    },
    [data, commit],
  );

  const addSale = useCallback(
    async (sale: Sale) => {
      await commit({ ...data, sales: [...data.sales, sale] });
    },
    [data, commit],
  );

  const addExpense = useCallback(
    async (expense: Expense) => {
      await commit({ ...data, expenses: [...data.expenses, expense] });
    },
    [data, commit],
  );

  const addReturn = useCallback(
    async (record: ReturnRecord) => {
      await commit({ ...data, returns: [...data.returns, record] });
    },
    [data, commit],
  );

  const addDocument = useCallback(
    async (doc: DocumentRecord) => {
      await commit({ ...data, documents: [...data.documents, doc] });
    },
    [data, commit],
  );

  const deleteDocument = useCallback(
    async (id: string) => {
      await commit({ ...data, documents: data.documents.filter((d) => d.id !== id) });
    },
    [data, commit],
  );

  const deletePurchase = useCallback(
    async (id: string) => {
      // Al borrar una compra se elimina también el stock (lotes) que generó.
      await commit({
        ...data,
        purchases: data.purchases.filter((p) => p.id !== id),
        lots: data.lots.filter((l) => l.purchaseId !== id),
      });
    },
    [data, commit],
  );

  const deleteSale = useCallback(
    async (id: string) => {
      // Al borrar una venta el stock consumido vuelve al inventario.
      await commit({ ...data, sales: data.sales.filter((s) => s.id !== id) });
    },
    [data, commit],
  );

  const deleteExpense = useCallback(
    async (id: string) => {
      await commit({ ...data, expenses: data.expenses.filter((e) => e.id !== id) });
    },
    [data, commit],
  );

  const deleteReturn = useCallback(
    async (id: string) => {
      await commit({ ...data, returns: data.returns.filter((r) => r.id !== id) });
    },
    [data, commit],
  );

  const deleteProduct = useCallback(
    async (productId: string) => {
      // Borra el producto y todo su stock (lotes asociados).
      await commit({
        ...data,
        products: data.products.filter((p) => p.id !== productId),
        lots: data.lots.filter((l) => l.productId !== productId),
      });
    },
    [data, commit],
  );

  const addAdjustment = useCallback(
    async (adj: Adjustment) => {
      await commit({ ...data, adjustments: [...data.adjustments, adj] });
    },
    [data, commit],
  );

  const upsertProduct = useCallback(
    async (product: Product) => {
      const exists = data.products.some((p) => p.id === product.id);
      const products = exists
        ? data.products.map((p) => (p.id === product.id ? product : p))
        : [...data.products, product];
      await commit({ ...data, products });
    },
    [data, commit],
  );

  const updateSettings = useCallback(
    async (patch: Partial<Settings>) => {
      const settings = { ...data.settings, ...patch };
      setData({ ...data, settings });
      await writeSettings(settings);
    },
    [data],
  );

  const resetAll = useCallback(async () => {
    const fresh = emptyData();
    const settings = data.settings;
    const next: AppData = { ...fresh, settings };
    setData(next);
    await persistAll(next);
  }, [data.settings]);

  const loadDemoData = useCallback(async () => {
    const seed = buildSeedData();
    const next: AppData = { ...seed, settings: data.settings };
    setData(next);
    await persistAll(next);
  }, [data.settings]);

  const importBackup = useCallback(async (incoming: AppData) => {
    const safe: AppData = {
      ...emptyData(),
      ...incoming,
      settings: { ...defaultSettings(), ...(incoming.settings || {}) },
    };
    setData(safe);
    await persistAll(safe);
    await writeSettings(safe.settings);
  }, []);

  const summary = useMemo(() => buildSummary(data), [data]);
  const stock = useMemo(() => buildStock(data), [data]);
  const expiring = useMemo(() => buildExpiring(data, 30), [data]);
  const timeline = useMemo(() => buildTimeline(data), [data]);

  const value: DataContextValue = {
    data,
    loading,
    error,
    summary,
    stock,
    expiring,
    timeline,
    reload,
    addPurchase,
    addSale,
    addExpense,
    addReturn,
    addDocument,
    deleteDocument,
    deletePurchase,
    deleteSale,
    deleteExpense,
    deleteReturn,
    deleteProduct,
    addAdjustment,
    upsertProduct,
    updateSettings,
    resetAll,
    loadDemoData,
    importBackup,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useData(): DataContextValue {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error("useData debe usarse dentro de DataProvider");
  return ctx;
}